<template>
    <TabComp
        class="h-100"
        :tabs="tabs"
        :content-fill="true"
        :pinable="false"
    >
        <template #tab-time>
            <MatrixStationRoleCellTimeTimeEditor
                :contestantId="contestantId"
                :raceContestantId="raceContestantId"
                :bib="bib"
                :stationRole="stationRole"
                :LapNumber="LapNumber"
                :raceContestantStartTime="raceContestantStartTime"
                :usedTime="usedTime"
                :editingTuid="editingTuid"
                :contestantTimeGetter="contestantTimeGetter"
                :StationIdToBackupStationIdsMap="StationIdToBackupStationIdsMap"
                ref="timeEditorRef"
            />
        </template>
        <template #tab-flag>
            <MatrixStationRoleCellTimeFlagEditor
                v-if="raceContestantId && exitStatus !== undefined"
                ref="flagEditorRef"
                :prefill-flag-time="
                    exitStatus === 'None' && raceContestantStartTime
                        ? new Date(raceContestantStartTime.getTime() + (props.usedTime ?? 0) + 5000)
                        : null
                "
                :prefill-date="raceContestantStartTime ? raceContestantStartTime : event.Date"
                :event-id="event.ID"
                :race-contestant-id="raceContestantId"
                :exit-status="exitStatus"
                :exit-status-flag-time="exitStatusFlagTime ?? null"
                :exit-status-reason="exitStatusReason ?? null"
            />
        </template>
    </TabComp>
</template>
<script setup lang="ts">
import { TabComp } from '@/components';
import type { ContestantTime, ExitStatus, StationRole } from '@/objects/generated';
import { useEventCommonStore } from '@/stores';
import { TabContainer, TabDefinition } from '@/objects/Tabs';
import MatrixStationRoleCellTimeFlagEditor from './MatrixStationRoleCellTimeFlagEditor.vue';
import MatrixStationRoleCellTimeTimeEditor from './MatrixStationRoleCellTimeTimeEditor.vue';
import { ref } from 'vue';

const props = defineProps<{
    /**
     * Id of the contestant we are editing the time for
     */
    contestantId?: number | null;

    /**
     * RacecontestantId of the contestant we are editing the time for
     */
    raceContestantId?: number | null;

    /**
     * The bib of the contestant we are editing the time for
     * If contestantId is not set, this will be used to add a new time for the contestant with this bib
     */
    bib?: number | null;

    /**
     * The stationRole of the cell we are editing
     */
    stationRole: StationRole;

    /**
     * Which lap we are on.
     * If this is supplied, we get the "Force time active" checkbox
     */
    LapNumber?: number;

    /**
     * The starttime of the raceContestant (used to calculate the used time)
     */
    raceContestantStartTime?: Date | null;

    /**
     * The current used time. This is essentially the value we are editing in this dialog, so this should have the current value
     */
    usedTime: number | null;

    /**
     * The TUID of the time we are editing if there is an existing time here
     */
    editingTuid: string | null;

    /**
     * Function to get the times of the contestant. This is used to fill the quick select dropdown
     */
    contestantTimeGetter: (contestantId: number) => ContestantTime[];

    /**
     * Map of backup stations
     */
    StationIdToBackupStationIdsMap: Map<number, number[]>;

    /**
     * ExitStatus of the contestant.
     * If this is undefined we hide the flag tab
     */
    exitStatus?: ExitStatus;

    /**
     * The ExitStatus flag time.
     */
    exitStatusFlagTime?: Date | null;

    /**
     * The user supplied reason why the exit status was set
     */
    exitStatusReason?: string | null;
}>();

const event = useEventCommonStore().Event!;

const tabs = new TabContainer();
tabs.AddTab(new TabDefinition('time', 'general.time', 'fa-stopwatch'));
if (props.raceContestantId) {
    tabs.AddTab(new TabDefinition('flag', 'general.flag', 'fa-flag-checkered'));
}

const timeEditorRef = ref<InstanceType<typeof MatrixStationRoleCellTimeTimeEditor>>();
const flagEditorRef = ref<InstanceType<typeof MatrixStationRoleCellTimeFlagEditor>>();

/**
 * Saves the time to the server
 * Disables any existing time if we are editing an existing time
 */
const save = async () => {
    let timeAddRequest = null;

    // Save the time if we have changed something on time
    if (timeEditorRef.value?.saveButtonEnabled()) {
        timeAddRequest = await timeEditorRef.value?.save();
    }

    // Save flags if we have changed something on flags
    if (flagEditorRef.value?.saveButtonEnabled()) {
        await flagEditorRef.value?.save();
    }

    return timeAddRequest;
};

// Disables the save button if we have not changed anything
const saveButtonEnabled = () => {
    return (timeEditorRef.value?.saveButtonEnabled() || flagEditorRef.value?.saveButtonEnabled()) ?? false;
};

defineExpose({
    save,
    saveButtonEnabled,
});
</script>
